import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, debounceTime, exhaustMap, filter, groupBy, map, mergeMap } from 'rxjs/operators';

import { klona } from 'klona/json';

import { GridService } from '@core/services';

import * as GridActions from './grid.actions';

@Injectable()
export class GridEffects {
  constructor(private actions$: Actions, private gridService: GridService) {}

  GridInitialize$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.Initialize),
      groupBy((action) => action.id),
      mergeMap((action$) =>
        action$.pipe(
          debounceTime(50),
          concatLatestFrom(({ id }) => [this.gridService.initialized$(id)]),
          filter(([_, initialized]) => !initialized),
          map(([{ id, grid }, _]) => {
            return GridActions.InitializeGridFromDefaults({ id, grid, defaults: grid });
          })
        )
      )
    );
  });

  GridInitializeComplete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.InitializeGridFromSync, GridActions.InitializeGridFromDefaults),
      map(({ id, grid, defaults }) => {
        return GridActions.InitializeGridComplete({ id, grid, defaults });
      })
    );
  });

  //
  // grid state save / load / remove
  //

  SaveRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.SaveRequest),
      concatLatestFrom(({ id }) => [this.gridService.grid$(id)]),
      exhaustMap(([{ id, saveId, saveName }, grid]) => {
        const data = klona(grid);

        // set any keys we don't want to store to defaults or null
        data.data = null;
        data.defaultAvailableColumns = null;
        data.defaultFilters = null;
        data.defaultSelectedColumns = null;
        data.defaultSorts = null;
        data.first = 0;
        data.page = 1;
        data.selectedRow = null;
        data.selectedRows = null;
        data.summaryRow = null;
        data.totalRows = 0;

        return this.gridService.saveGrid({ gridId: id, saveId, saveName, data }).pipe(
          map((status) => GridActions.SaveRequestSuccess({ id, saveId, saveName, data: status })),
          catchError((error) => of(GridActions.SaveRequestFailure(error)))
        );
      })
    );
  });

  ListRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.ListRequest),
      exhaustMap(({ id }) => {
        return this.gridService.listGrids({ gridId: id }).pipe(
          map((list) => GridActions.ListRequestSuccess({ id, list })),
          catchError((error) => of(GridActions.ListRequestFailure(error)))
        );
      })
    );
  });

  RemoveRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.RemoveRequest),
      exhaustMap(({ id, saveId }) => {
        return this.gridService.removeGrid({ gridId: id, saveId }).pipe(
          map((status) => GridActions.RemoveRequestSuccess({ id, saveId, data: status })),
          catchError((error) => of(GridActions.RemoveRequestFailure(error)))
        );
      })
    );
  });

  RemoveRequestSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.RemoveRequestSuccess),
      map(({ id }) => GridActions.ListRequest({ id }))
    );
  });

  SaveConflictCheckRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.SaveConflictCheckRequest),
      exhaustMap(({ id, saveId, saveName }) => {
        return this.gridService.saveGridConflictCheck({ gridId: id, saveId, saveName }).pipe(
          map((status) => GridActions.SaveConflictCheckRequestSuccess({ id, saveId, saveName, data: status })),
          catchError((error) => of(GridActions.SaveConflictCheckRequestFailure(error)))
        );
      })
    );
  });

  SaveConflictCheckRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(GridActions.SaveConflictCheckRequestSuccess),
        map(({ data }) => {
          this.gridService.gridSaveConflictCheckResult$.next(data);
        })
      );
    },
    { dispatch: false }
  );

  LoadRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.LoadRequest),
      filter(({ saveId }) => saveId !== 'default'),
      exhaustMap(({ id, saveId, saveName }) => {
        return this.gridService.loadGrid({ gridId: id, saveId }).pipe(
          map((data) => GridActions.LoadRequestSuccess({ id, saveId, saveName, data })),
          catchError((error) => of(GridActions.LoadRequestFailure(error)))
        );
      })
    );
  });

  LoadRequestDefault$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.LoadRequest),
      filter(({ saveId }) => saveId === 'default'),
      map(({ id }) => GridActions.Reset({ id }))
    );
  });

  LoadRequestSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GridActions.LoadRequestSuccess),
      map(({ id, saveId, saveName, data }) => {
        return GridActions.LoadSettings({ id, saveId, saveName, grid: data });
      })
    );
  });

  LoadSettings$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(GridActions.LoadSettings),
        map(({ id }) => {
          this.gridService.updateColumnHeaders(id);
          this.gridService.gridLoad$.next(id);
        })
      );
    },
    { dispatch: false }
  );

  Reset$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(GridActions.Reset),
        map(({ id }) => {
          this.gridService.updateColumnHeaders(id);
        })
      );
    },
    { dispatch: false }
  );
}
